import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useBlocker } from "react-router";

import ModalGeneric from "../components/Common/ModalGeneric";
import AuthorizedRoute from "../components/Routes/AuthorizedRoutes";

import IleggelseOrginalbilde from "../components/Common/Ileggelser/Detalj/IleggelseOrginalbilde";
import IleggelseImageEditorPageContainer from "../components/Common/Ileggelser/Detalj/ImageEditorPageContainer";
import MiljogebyrImageEditorPageContainer from "../components/Common/Miljogebyr/Detalj/ImageEditorPageContainer";
import MiljogebyrOrginalbilde from "../components/Common/Miljogebyr/Detalj/MiljogebyrOrginalbilde";
import TidskontrollOrginalbilde from "../components/Common/Miljogebyr/Detalj/TidskontrollOrginalbilde";
import IleggelseDetaljPage from "../components/Ileggelse/Detalj/IleggelseDetaljPage";
import KlagePage from "../components/Klage/KlagePage";
import MiljogebyrDetaljPage from "../components/Miljogebyr/Detalj/MiljogebyrDetaljPage";
import MiljokontrollDetaljPage from "../components/Miljogebyr/Miljokontroll/Detalj/MiljokontrollDetaljPage";

import { bypatruljen, juridisk, juridiskAll } from "../constants/roles";
import sitePath from "../constants/sitePaths";
import { authorized } from "../utils/authorizationUtils";

const ALL = [...Object.values(bypatruljen), ...Object.values(juridisk)];
const EDIT = [
  bypatruljen.saksbehandleradmin,
  bypatruljen.saksbehandler,
  juridisk.saksbehandler,
];

const FALLBACK_BLOCKER = {
  state: "unblocked",
  proceed: () => {},
  reset: () => {},
};

/** @param {(arg0: { currentLocation: { pathname: string } }) => boolean} shouldBlock */
function useOptionalBlocker(shouldBlock) {
  try {
    return useBlocker(shouldBlock);
  } catch {
    return FALLBACK_BLOCKER;
  }
}

const Klage = () => {
  const vedtaker = useSelector((state) => state.vedtaker);
  const [urlTransitionModalTitle, setUrlTransitionModalTitle] = useState("");
  const [urlTransitionModalBodyText, setUrlTransitionModalBodyText] =
    useState("");

  const hasIkkeFerdigbehandletVedtaker = () => {
    if (!authorized(juridiskAll)) return false;
    if (!vedtaker) return false;
    return Object.values(vedtaker).some((v) => v.vedtaksDato === null);
  };

  /** @param {{ currentLocation: { pathname: string } }} param0 */
  const shouldBlock = ({ currentLocation }) =>
    currentLocation.pathname === sitePath.klage.shortUrl &&
    hasIkkeFerdigbehandletVedtaker();

  const blocker = useOptionalBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setUrlTransitionModalTitle(
        "Det finnes vedtak som ikke er ferdigbehandlet",
      );
      setUrlTransitionModalBodyText(
        "Vil du gå videre uten å ferdigbehandle vedtakene?",
      );
    }
  }, [blocker.state]);

  const handleCloseUrlTransitionModal = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  const handleFortsettUrlTransitionModal = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  return (
    <>
      {blocker.state === "blocked" && (
        <ModalGeneric
          id="urlTransitionModal"
          modalTitle={urlTransitionModalTitle}
          open={true}
          openModal={() => {}}
          closeModal={handleCloseUrlTransitionModal}
          submit={handleFortsettUrlTransitionModal}
          submitButtonText="Fortsett"
        >
          <p>{urlTransitionModalBodyText}</p>
        </ModalGeneric>
      )}
      <Routes>
        {/* klage list page */}
        <Route
          index
          element={
            <AuthorizedRoute
              roles={[
                bypatruljen.saksbehandleradmin,
                ...Object.values(juridisk),
              ]}
            >
              <KlagePage />
            </AuthorizedRoute>
          }
        />

        {/* ileggelse detalj routes */}
        <Route path="ileggelser/:id">
          <Route
            index
            element={
              <AuthorizedRoute roles={ALL}>
                <IleggelseDetaljPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path="bilder/:bildeId"
            element={
              <AuthorizedRoute roles={ALL}>
                <IleggelseOrginalbilde />
              </AuthorizedRoute>
            }
          />
          <Route
            path="bilder/:bildeId/rediger"
            element={
              <AuthorizedRoute roles={EDIT}>
                <IleggelseImageEditorPageContainer />
              </AuthorizedRoute>
            }
          />
        </Route>

        {/* miljogebyr detalj routes */}
        <Route path="miljogebyrer/:id">
          <Route
            index
            element={
              <AuthorizedRoute roles={ALL}>
                <MiljogebyrDetaljPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path="bilder/:bildeId"
            element={
              <AuthorizedRoute roles={ALL}>
                <MiljogebyrOrginalbilde />
              </AuthorizedRoute>
            }
          />
          <Route
            path="bilder/:bildeId/rediger"
            element={
              <AuthorizedRoute roles={EDIT}>
                <MiljogebyrImageEditorPageContainer />
              </AuthorizedRoute>
            }
          />
          <Route
            path="tidskontroller/:miljokontrollId/observasjoner/:observasjonId/bilder/:bildeId"
            element={
              <AuthorizedRoute roles={ALL}>
                <TidskontrollOrginalbilde />
              </AuthorizedRoute>
            }
          />
          <Route
            path="tidskontroller/:miljokontrollId/observasjoner/:observasjonId/bilder/:bildeId/rediger"
            element={
              <AuthorizedRoute roles={EDIT}>
                <MiljogebyrImageEditorPageContainer />
              </AuthorizedRoute>
            }
          />
        </Route>

        {/* miljokontroll detalj (nested under miljogebyr) */}
        <Route
          path="miljogebyrer/:miljogebyrId/tidskontroller/:id"
          element={
            <AuthorizedRoute roles={EDIT}>
              <MiljokontrollDetaljPage />
            </AuthorizedRoute>
          }
        />
      </Routes>
    </>
  );
};

export default Klage;
